import React from "react"

import { CaretIcon } from "components/Elements/"
import "./Carousel.scoped.scss"

import {
	motion,
	animate,
	AnimationOptions,
	PanInfo,
	useMotionValue,
	MotionValue,
	useReducedMotion,
} from "framer-motion"

export type CarouselProps = {
	children: React.ReactNode
	// Options
	className?: string
	defaultCurrentSlideIndex?: number
	autoPlay?: boolean
	interval?: number
	// Use specific components
	renderArrowLeft?: (args: { handlePrev: () => void; activeIndex: number }) => React.ReactNode
	renderArrowRight?: (args: { handleNext: () => void; activeIndex: number }) => React.ReactNode
	renderDots?: (args: Omit<CarouselDotsProps, "length">) => React.ReactNode
	// Handler
	onSlideChange?: (...args: unknown[]) => void
	carouselSlidesLength: number
}

export const Carousel: React.FC<CarouselProps> = ({
	children,
	className = "",
	autoPlay = true,
	interval = 2000,
	defaultCurrentSlideIndex = 0,
	renderArrowLeft,
	renderArrowRight,
	renderDots,
	onSlideChange,
	carouselSlidesLength,
}) => {
	const [currentSlideIndex, setCurrentSlideIndex] =
		React.useState<number>(defaultCurrentSlideIndex)

	// Accessibility
	const shouldReduceMotion = useReducedMotion()

	const x = useMotionValue(0)
	const carouselRef = React.useRef<HTMLDivElement>(null)
	const calculateNewX = () => -currentSlideIndex * (carouselRef.current?.clientWidth || 0)

	const childrens = React.Children.toArray(children)

	const isOnlyOneSlide = carouselSlidesLength === 1

	// animation config
	const getTransition: () => AnimationOptions<number> = () => {
		if (shouldReduceMotion) return { duration: 0 }
		return {
			type: "spring",
			bounce: 0,
			duration: 0.6,
		}
	}

	const handleEndDrag = (event: Event, dragProps: PanInfo) => {
		const clientWidth = carouselRef.current?.clientWidth || 0
		const { offset } = dragProps

		if (offset.x > clientWidth / 4) {
			handlePrev()
		} else if (offset.x < -clientWidth / 4) {
			handleNext()
		} else {
			animate(x, calculateNewX(), getTransition())
		}
	}

	const handleNext = () => {
		setCurrentSlideIndex((prevSlideIndex) =>
			prevSlideIndex + 1 === childrens.length ? 0 : prevSlideIndex + 1
		)
	}

	const handlePrev = () => {
		setCurrentSlideIndex((prevSlideIndex) =>
			prevSlideIndex - 1 < 0 ? childrens.length - 1 : prevSlideIndex - 1
		)
	}

	React.useEffect(() => {
		// trigger handler on slide change
		if (onSlideChange) onSlideChange(currentSlideIndex)
		// imperatively trigger slide animation
		const controls = animate(x, calculateNewX(), getTransition())
		// clean animation on unmount
		return controls.stop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentSlideIndex])

	React.useEffect(() => {
		if (!autoPlay) return
		const timer = setInterval(() => handleNext(), interval)
		return clearInterval(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [handleNext, interval])

	return (
		<div className={`c-carousel ${className}`} ref={carouselRef}>
			<div className="c-carousel__body">
				{childrens.map((child, i) => (
					<CarouselSlide
						onDragEnd={handleEndDrag}
						x={x}
						i={i}
						key={`carousel-slide-${i}`}
					>
						{child}
					</CarouselSlide>
				))}
			</div>

			{!isOnlyOneSlide && (
				<div className="c-carousel__footer my-4 md:mt-16">
					<div className="c-carousel__nav">
						{/* left arrow */}
						{renderArrowLeft &&
							renderArrowLeft({ handlePrev, activeIndex: currentSlideIndex })}
						{!renderArrowLeft && <CarouselArrow onClick={handlePrev} label="Prev" />}

						{/* left arrow */}
						{renderArrowRight &&
							renderArrowRight({ handleNext, activeIndex: currentSlideIndex })}
						{!renderArrowRight && (
							<CarouselArrow onClick={handleNext} label="Next" left={true} />
						)}
					</div>

					{/* dots */}

					{renderDots ? (
						renderDots({
							setActiveIndex: setCurrentSlideIndex,
							activeIndex: currentSlideIndex,
						})
					) : (
						<CarouselDots
							length={childrens.length}
							setActiveIndex={setCurrentSlideIndex}
							activeIndex={currentSlideIndex}
						/>
					)}
				</div>
			)}
		</div>
	)
}

// Slides
// --------
type CarouselSlideProps = {
	x: MotionValue<number>
	i: number
	children: React.ReactNode
	onDragEnd: (event: Event, dragProps: PanInfo) => void
}

const CarouselSlide = ({ x, i, onDragEnd, children }: CarouselSlideProps) => (
	<motion.div
		className={"c-carousel__slide"}
		key={`carousel-slide-${i}`}
		style={{
			x,
			left: `${i * 100}%`,
			right: `${i * 100}%`,
		}}
		drag="x"
		dragElastic={0.3}
		onDragEnd={onDragEnd}
	>
		{children}
	</motion.div>
)

type CarouselArrowProps = {
	onClick: () => void
	left?: boolean
	label: string
}

// Arrow
// --------
const CarouselArrow = ({ left = false, label, onClick }: CarouselArrowProps) => (
	<button onClick={onClick} aria-label={label} className="p-4 mx-2">
		<CaretIcon
			style={{ transform: left ? "" : "rotate(180deg)" }}
			size="30"
			color="var(--white)"
		/>
	</button>
)

// Dots
// --------
type CarouselDotsProps = {
	length: number
	activeIndex: number
	setActiveIndex: (index: number) => void
}

const CarouselDots = ({ length, activeIndex, setActiveIndex }: CarouselDotsProps) => {
	return (
		<div className="c-carousel__indicators">
			{Array.from({ length }, (_, i) => i).map((_, i) => (
				<span
					onClick={() => setActiveIndex(i)}
					onKeyDown={() => setActiveIndex(i)}
					className={
						"c-bullet c-bullet--big m-2 " + (i === activeIndex ? "" : "c-bullet--empty")
					}
					key={"bullet" + i}
					role="button"
					tabIndex={0}
					style={{
						transform: `scale(${i === activeIndex ? 1.3 : 1})`,
					}}
				></span>
			))}
		</div>
	)
}
